import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { useLayout } from "../../../theme/LayoutContext";
import isScreen from "../../utils/screenHelper";
import LinksGroup from "./LinksGroup/LinksGroup";
import companyLogo from "../../../assets/images/logo.png";
import companyMiniLogo from "../../../assets/images/logo-mini.png";
import s from "./Sidebar.module.scss";

interface MenuItem {
  header: string;
  link?: string;
  iconName?: string;
  index?: string;
  target?: string;
  exact?: boolean;
  childrenLinks?: MenuItem[];
  [key: string]: any;
}

function buildMenuItems(userData: Record<string, any> | null): MenuItem[] {
  if (!userData) return [];

  const helpExternal = {
    header: "Help",
    link: "https://lovestrongmarriage.com/?page_id=6",
    iconName: "mdi mdi-help-circle-outline",
    target: "_blank",
    index: "helper",
  };

  const upcomingRetreatsItem = {
    header: "Upcoming Retreats",
    link: "/upcoming-retreats",
    iconName: "mdi mdi-calendar-clock",
    index: "upcoming-retreats",
  };

  // Full ADMIN menu
  if (userData.userRole === "ADMIN" && userData.isDisabled === 0) {
    return [
      {
        header: "Dashboard",
        link: "/dashboard",
        iconName: "mdi mdi-view-dashboard-outline",
        index: "dashboard",
        exact: true,
      },
      {
        header: "Quick Reports",
        link: "/quickReport",
        iconName: "mdi mdi-grid-large",
        index: "quickReport",
      },
      {
        header: "Advanced Search",
        link: "/search_data",
        iconName: "mdi mdi-magnify",
        index: "search_data",
      },
      {
        header: "Manage Couples",
        link: "/manageCouples",
        iconName: "mdi mdi-account-multiple",
        index: "manageCouples",
      },
      {
        header: "Manage Retreats",
        link: "/manageRetreat",
        iconName: "mdi mdi-church",
        index: "manageRetreat",
      },
      {
        header: "Retreat Closure",
        link: "/retreat-closure-list",
        iconName: "mdi mdi-close-network",
        index: "retreat-closure-list",
      },
      {
        header: "Broadcast Emails",
        link: "/sendEmail",
        iconName: "mdi mdi-email",
        index: "sendEmail",
      },
      {
        header: "Log Report",
        link: "/logreport",
        iconName: "mdi mdi-file-document-box-outline",
        index: "logreport",
      },
      helpExternal,
      {
        header: "Tickets",
        link: "/tickets",
        iconName: "mdi mdi-ticket-outline",
        index: "tickets",
      },
      {
        header: "Master Data",
        link: "/master-data",
        iconName: "mdi mdi-table",
        index: "master-data",
        childrenLinks: [
          { header: "Parishes", link: "/parish", index: "parish" },
          { header: "Services", link: "/service", index: "service" },
          { header: "Roles", link: "/roles", index: "roles" },
          { header: "Talks", link: "/talks", index: "talks" },
          {
            header: "Parish Categories",
            link: "/parishCategories",
            index: "parishCategories",
          },
          {
            header: "Deaneries",
            link: "/deaneries",
            index: "deaneries",
          },
          {
            header: "Sample Content",
            link: "/sample-content",
            index: "sample-content",
          },
        ],
      },
      {
        header: "Master Report Section",
        link: "/master-report",
        iconName: "mdi mdi-table",
        index: "master-report",
        childrenLinks: [
          {
            header: "Missing Parish Data",
            link: "/missing-data",
            index: "missing-data",
          },
        ],
      },
      {
        header: "Participated In Retreats",
        link: "/participated-in-retreats",
        iconName: "mdi mdi-church",
        index: "participated-in-retreats",
      },
      upcomingRetreatsItem,
      {
        header: "Update Your Profile",
        link: "/update-couple",
        iconName: "mdi mdi-account-multiple",
        index: "update-couple",
      },
    ];
  }

  // ADMIN disabled
  if (userData.userRole === "ADMIN" && userData.isDisabled === 1) {
    const items = [];
    if (userData.coupleId !== null) {
      items.push(
        {
          header: "Update Your Profile",
          link: "/update-couple",
          iconName: "mdi mdi-account-multiple",
          index: "update-couple",
        },
        {
          header: "Participated In Retreats",
          link: "/participated-in-retreats",
          iconName: "mdi mdi-church",
          index: "participated-in-retreats",
        }
      );
    }
    items.push(upcomingRetreatsItem);
    if (userData.access === true) {
      items.push({
        header: "Manage Retreats",
        link: "/manageRetreat",
        iconName: "mdi mdi-church",
        index: "manageRetreat",
      });
    }
    return items;
  }

  // SUBADMIN enabled
  if (userData.userRole === "SUBADMIN" && userData.isDisabled === 0) {
    const items = [
      {
        header: "Dashboard",
        link: "/dashboard",
        iconName: "mdi mdi-view-dashboard-outline",
        index: "dashboard",
        exact: true,
      },
      {
        header: "Update Your Profile",
        link: "/update-couple",
        iconName: "mdi mdi-account-multiple",
        index: "update-couple",
      },
      {
        header: "Participated In Retreats",
        link: "/participated-in-retreats",
        iconName: "mdi mdi-church",
        index: "participated-in-retreats",
      },
      upcomingRetreatsItem,
      {
        header: "View Couples Data",
        link: "/manageCouples",
        iconName: "mdi mdi-account-multiple",
        index: "manageCouples",
      },
    ];
    if (userData.committeeMember === true || userData.access === true) {
      items.push({
        header: "Manage Retreats",
        link: "/manageRetreat",
        iconName: "mdi mdi-church",
        index: "manageRetreat",
      });
    }
    items.push(helpExternal);
    return items;
  }

  // SUBADMIN disabled
  if (userData.userRole === "SUBADMIN" && userData.isDisabled === 1) {
    const items = [];
    if (userData.coupleId !== null) {
      items.push(
        {
          header: "Update Your Profile",
          link: "/update-couple",
          iconName: "mdi mdi-account-multiple",
          index: "update-couple",
        },
        {
          header: "Participated In Retreats",
          link: "/participated-in-retreats",
          iconName: "mdi mdi-church",
          index: "participated-in-retreats",
        }
      );
    }
    items.push(upcomingRetreatsItem);
    if (userData.access === true) {
      items.push({
        header: "Manage Retreats",
        link: "/manageRetreat",
        iconName: "mdi mdi-church",
        index: "manageRetreat",
      });
    }
    return items;
  }

  // COUPLE
  if (userData.userRole === "COUPLE") {
    const items = [];
    if (userData.coupleId !== null) {
      items.push(
        {
          header: "Update Your Profile",
          link: "/update-couple",
          iconName: "mdi mdi-account-multiple",
          index: "update-couple",
        },
        {
          header: "Participated In Retreats",
          link: "/participated-in-retreats",
          iconName: "mdi mdi-church",
          index: "participated-in-retreats",
        }
      );
    }
    items.push(upcomingRetreatsItem);
    if (
      userData.committeeMember === true ||
      (userData.access === true && userData.isDisabled === 0)
    ) {
      items.push({
        header: "Manage Retreats",
        link: "/manageRetreat",
        iconName: "mdi mdi-church",
        index: "manageRetreat",
      });
    }
    return items;
  }

  return [upcomingRetreatsItem];
}

function pathMatches(pathname: string, link?: string): boolean {
  if (!link) return false;
  return pathname === link || pathname.startsWith(`${link}/`);
}

function findActiveMenuKey(items: MenuItem[], pathname: string): string {
  for (const item of items) {
    if (
      item.childrenLinks &&
      item.childrenLinks.some(
        (child) =>
          pathMatches(pathname, child.link) ||
          (child.childrenLinks &&
            child.childrenLinks.some((g) => pathMatches(pathname, g.link)))
      )
    ) {
      return item.link || item.index || pathname;
    }
  }
  return pathname;
}

function Sidebar(): JSX.Element {
  const location = useLocation();
  const {
    sidebarOpened,
    sidebarStatic,
    activeItem,
    openSidebar,
    closeSidebar,
    changeActiveSidebarItem,
  } = useLayout();

  const userData: Record<string, any> | null = useAuthUser();
  const menuItems = useMemo(() => buildMenuItems(userData), [userData]);

  const onMouseEnter = () => {
    if (!sidebarStatic && (isScreen("lg") || isScreen("xl"))) {
      openSidebar();
      changeActiveSidebarItem(
        findActiveMenuKey(menuItems, location.pathname)
      );
    }
  };

  const onMouseLeave = () => {
    if (!sidebarStatic && (isScreen("lg") || isScreen("xl"))) {
      closeSidebar();
      // Sing App clears active item so all submenus collapse
      changeActiveSidebarItem(null);
    }
  };

  const onNavigate = () => {
    if (window.innerWidth <= 767.98) {
      closeSidebar();
      changeActiveSidebarItem(null);
    }
  };

  const isClosed = !sidebarOpened && !sidebarStatic;
  const sidebarExpanded = !isClosed;

  return (
    <div
      className={`sing-sidebar ${isClosed ? s.sidebarClose : ""} ${s.sidebarWrapper}`}
      data-sidebar-closed={isClosed ? "true" : "false"}
    >
      <nav
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={`sing-sidebar-nav ${s.root}`}
      >
        <header className={s.logo}>
          <Link to="/" className={s.brandLink}>
            <img
              src={isClosed ? companyMiniLogo : companyLogo}
              alt="LOVESTRONG Marriage"
              className={isClosed ? s.logoMiniImg : s.logoImg}
            />
          </Link>
        </header>
        <ul className={s.nav}>
          {menuItems.map((item) => (
            <LinksGroup
              key={item.index || item.link}
              onActiveSidebarItemChange={changeActiveSidebarItem}
              activeItem={activeItem}
              header={item.header}
              isHeader
              iconName={item.iconName}
              link={item.link}
              index={item.index}
              childrenLinks={item.childrenLinks}
              target={item.target}
              onNavigate={onNavigate}
              exact={item.exact}
              sidebarExpanded={sidebarExpanded}
            />
          ))}
        </ul>
      </nav>
    </div>
  );
}

export default Sidebar;
